import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { toast } from 'react-toastify';
import { shouldSuppressRateLimit } from '../utils/httpErrors';
import { COLORS } from '../config/theme';

function SectionCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-2xl border shadow-sm p-6" style={{ borderColor: COLORS.border }}>
      <div className="mb-4">
        <h2 className="text-xl font-semibold" style={{ color: COLORS.text }}>{title}</h2>
        {description && (
          <p className="text-sm mt-1" style={{ color: COLORS.text2 }}>{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Button({ children, onClick, disabled, variant = 'primary' }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor: isPrimary ? COLORS.primary : 'transparent',
        color: isPrimary ? '#fff' : COLORS.primary,
        border: isPrimary ? 'none' : `1px solid ${COLORS.primary}`,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (isPrimary) e.currentTarget.style.backgroundColor = COLORS.primaryHover;
        else e.currentTarget.style.backgroundColor = COLORS.softBlue;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        if (isPrimary) e.currentTarget.style.backgroundColor = COLORS.primary;
        else e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

function CronForm({ value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Es: 0 2 * * *"
      className="w-full px-3 py-2 rounded-lg border text-sm"
      style={{ borderColor: COLORS.border }}
    />
  );
}

export default function SettingsPage() {
  const token = localStorage.getItem('access_token');

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: token ? `Bearer ${token}` : undefined },
  }), [token]);

  // Run-now scraping
  const [scrapeTaskId, setScrapeTaskId] = useState(null);
  const [scrapeTaskStatus, setScrapeTaskStatus] = useState(null);

  // Schedules
  const [scrapingSchedules, setScrapingSchedules] = useState([]);
  const [notificationSchedules, setNotificationSchedules] = useState([]);
  const [archiveStats, setArchiveStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Simple create/edit for scraping schedules
  const [scrapingForm, setScrapingForm] = useState({
    id: null,
    name: '',
    schedule_type: 'daily',
    hour: 2,
    minute: 0,
    day_of_week: '1',
    custom_cron: '',
    is_active: true,
  });

  // Simple edit for notification schedules (admin)
  const [notifEdit, setNotifEdit] = useState(null); // {id, name, schedule_type,..., filters_json}

  const loadAll = async () => {
    setLoading(true);
    try {
      const [scr, notif, stats] = await Promise.all([
        api.get('/admin/settings/scraping-schedules'),
        api.get('/admin/settings/notification-schedules'),
        api.get('/admin/settings/archive/stats'),
      ]);
      setScrapingSchedules(scr.data || []);
      setNotificationSchedules(notif.data || []);
      setArchiveStats(stats.data || null);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scrapeTaskId) return;
    const timer = setInterval(async () => {
      try {
        const res = await api.get(`/scraping/tasks/${scrapeTaskId}`);
        setScrapeTaskStatus(res.data);
      } catch (e) {
        // se fallisce, non spammiamo
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [scrapeTaskId, api]);

  const startScrapingNow = async () => {
    try {
      setScrapeTaskId(null);
      setScrapeTaskStatus(null);
      const res = await api.post('/admin/settings/scraping/pubblicitalegale/run-now');
      setScrapeTaskId(res.data.task_id);
      toast.success('Scraping avviato');
    } catch (e) {
      if (shouldSuppressRateLimit(e)) {
        return;
      }
      toast.error(e.response?.data?.detail || 'Errore avvio scraping');
    }
  };

  const submitScrapingSchedule = async () => {
    try {
      const payload = {
        name: scrapingForm.name,
        schedule_type: scrapingForm.schedule_type,
        hour: Number(scrapingForm.hour),
        minute: Number(scrapingForm.minute),
        day_of_week: scrapingForm.day_of_week,
        custom_cron: scrapingForm.custom_cron || null,
        is_active: Boolean(scrapingForm.is_active),
      };
      if (scrapingForm.id) {
        await api.put(`/admin/settings/scraping-schedules/${scrapingForm.id}`, payload);
        toast.success('Schedulazione scraping aggiornata');
      } else {
        await api.post('/admin/settings/scraping-schedules', payload);
        toast.success('Schedulazione scraping creata');
      }
      setScrapingForm({ id: null, name: '', schedule_type: 'daily', hour: 2, minute: 0, day_of_week: '1', custom_cron: '', is_active: true });
      await loadAll();
    } catch (e) {
      if (shouldSuppressRateLimit(e)) {
        return;
      }
      toast.error(e.response?.data?.detail || 'Errore salvataggio schedulazione scraping');
    }
  };

  const toggleScrapingSchedule = async (id) => {
    try {
      await api.post(`/admin/settings/scraping-schedules/${id}/toggle`);
      await loadAll();
    } catch (e) {
      toast.error('Errore toggle schedulazione scraping');
    }
  };

  const runScrapingScheduleNow = async (id) => {
    try {
      const res = await api.post(`/admin/settings/scraping-schedules/${id}/run-now`);
      toast.success(`Job accodato: ${res.data.task_id}`);
      await loadAll();
    } catch (e) {
      toast.error('Errore run-now scraping schedulato');
    }
  };

  const deleteScrapingSchedule = async (id) => {
    if (!window.confirm('Eliminare questa schedulazione scraping?')) return;
    try {
      await api.delete(`/admin/settings/scraping-schedules/${id}`);
      toast.success('Schedulazione eliminata');
      await loadAll();
    } catch (e) {
      toast.error('Errore eliminazione schedulazione');
    }
  };

  const openNotifEdit = (row) => {
    setNotifEdit({
      id: row.id,
      name: row.name,
      schedule_type: row.schedule_type || 'custom',
      hour: 8,
      minute: 0,
      day_of_week: '1',
      custom_cron: row.cron_expression,
      is_active: row.is_active,
      filters_json: '', // opzionale
    });
  };

  const saveNotifEdit = async () => {
    try {
      let filters = null;
      if (notifEdit.filters_json && notifEdit.filters_json.trim()) {
        filters = JSON.parse(notifEdit.filters_json);
      }
      const payload = {
        name: notifEdit.name,
        schedule_type: notifEdit.schedule_type,
        hour: Number(notifEdit.hour),
        minute: Number(notifEdit.minute),
        day_of_week: notifEdit.day_of_week,
        custom_cron: notifEdit.custom_cron || null,
        is_active: Boolean(notifEdit.is_active),
        filters,
      };
      await api.put(`/admin/settings/notification-schedules/${notifEdit.id}`, payload);
      toast.success('Schedulazione email aggiornata');
      setNotifEdit(null);
      await loadAll();
    } catch (e) {
      if (shouldSuppressRateLimit(e)) {
        return;
      }
      toast.error(e.response?.data?.detail || 'Errore aggiornamento schedulazione email');
    }
  };

  const toggleNotifSchedule = async (id) => {
    try {
      await api.post(`/admin/settings/notification-schedules/${id}/toggle`);
      await loadAll();
    } catch (e) {
      toast.error('Errore toggle schedulazione email');
    }
  };

  const runNotifNow = async (id) => {
    try {
      const res = await api.post(`/admin/settings/notification-schedules/${id}/run-now`);
      toast.success(`Job email accodato: ${res.data.task_id}`);
    } catch (e) {
      toast.error('Errore run-now email schedulata');
    }
  };

  const runArchiveNow = async () => {
    try {
      const res = await api.post('/admin/settings/archive/run-now');
      if (res.data?.success) toast.success(res.data.message || 'Archiviazione completata');
      else toast.error(res.data?.error || 'Errore archiviazione');
      await loadAll();
    } catch (e) {
      toast.error('Errore archiviazione');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: COLORS.text }}>Impostazioni (Admin)</h1>
        <p className="mt-2 text-sm" style={{ color: COLORS.text2 }}>
          Gestione scraping PubblicitàLegale, schedulazioni e archiviazione automatica.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <SectionCard
          title="Scraping PubblicitàLegale (run-now)"
          description="Avvia subito lo scraping e l’ingest diretto nel DB."
        >
          <div className="flex items-center gap-3">
            <Button onClick={startScrapingNow} disabled={loading}>Avvia scraping</Button>
            {scrapeTaskId && (
              <span className="text-xs" style={{ color: COLORS.text2 }}>
                task_id: <code>{scrapeTaskId}</code>
              </span>
            )}
          </div>
          {scrapeTaskStatus && (
            <pre className="mt-4 p-3 rounded-lg text-xs overflow-auto" style={{ background: COLORS.bg }}>
{JSON.stringify(scrapeTaskStatus, null, 2)}
            </pre>
          )}
        </SectionCard>

        <SectionCard
          title="Schedulazioni scraping (PubblicitàLegale)"
          description="Crea/modifica le schedulazioni: verranno eseguite da Celery Beat leggendo SchedulerConfig."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Nome</label>
              <input
                value={scrapingForm.name}
                onChange={(e) => setScrapingForm((p) => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm mt-1"
                style={{ borderColor: COLORS.border }}
              />
            </div>
            <div>
              <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Tipo schedulazione</label>
              <select
                value={scrapingForm.schedule_type}
                onChange={(e) => setScrapingForm((p) => ({ ...p, schedule_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm mt-1"
                style={{ borderColor: COLORS.border }}
              >
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="custom">custom</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Ora</label>
                <input type="number" min="0" max="23" value={scrapingForm.hour}
                  onChange={(e) => setScrapingForm((p) => ({ ...p, hour: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }} />
              </div>
              <div>
                <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Minuto</label>
                <input type="number" min="0" max="59" value={scrapingForm.minute}
                  onChange={(e) => setScrapingForm((p) => ({ ...p, minute: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }} />
              </div>
              <div>
                <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Giorno (weekly)</label>
                <input value={scrapingForm.day_of_week}
                  onChange={(e) => setScrapingForm((p) => ({ ...p, day_of_week: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Custom cron</label>
              <div className="mt-1">
                <CronForm value={scrapingForm.custom_cron} onChange={(v) => setScrapingForm((p) => ({ ...p, custom_cron: v }))} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input type="checkbox" checked={scrapingForm.is_active} onChange={(e) => setScrapingForm((p) => ({ ...p, is_active: e.target.checked }))} />
                <span className="text-xs" style={{ color: COLORS.text2 }}>Attiva</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <Button onClick={submitScrapingSchedule} disabled={!scrapingForm.name}>Salva</Button>
            {scrapingForm.id && (
              <Button variant="outline" onClick={() => setScrapingForm({ id: null, name: '', schedule_type: 'daily', hour: 2, minute: 0, day_of_week: '1', custom_cron: '', is_active: true })}>
                Annulla
              </Button>
            )}
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: COLORS.text2 }}>
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">Cron</th>
                  <th className="py-2 pr-4">Attiva</th>
                  <th className="py-2 pr-4">Next run</th>
                  <th className="py-2 pr-4">Last run</th>
                  <th className="py-2 pr-4">Ingest (lotti)</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {scrapingSchedules.map((s) => (
                  <tr key={s.id} className="border-t" style={{ borderColor: COLORS.border }}>
                    <td className="py-2 pr-4">{s.name}</td>
                    <td className="py-2 pr-4"><code>{s.cron_expression}</code></td>
                    <td className="py-2 pr-4">{s.is_active ? 'SI' : 'NO'}</td>
                    <td className="py-2 pr-4">{s.next_run || '—'}</td>
                    <td className="py-2 pr-4">{s.last_run || '—'}</td>
                    <td className="py-2 pr-4">
                      {s.last_ingest?.total_lotti_inserted ?? '—'}
                    </td>
                    <td className="py-2 pr-4 flex gap-2">
                      <Button variant="outline" onClick={() => {
                        setScrapingForm({
                          id: s.id,
                          name: s.name,
                          schedule_type: s.schedule_type || 'custom',
                          hour: 2,
                          minute: 0,
                          day_of_week: '1',
                          custom_cron: s.cron_expression,
                          is_active: s.is_active,
                        });
                      }}>Modifica</Button>
                      <Button variant="outline" onClick={() => toggleScrapingSchedule(s.id)}>Toggle</Button>
                      <Button variant="outline" onClick={() => runScrapingScheduleNow(s.id)}>Run-now</Button>
                      <Button variant="outline" onClick={() => deleteScrapingSchedule(s.id)}>Elimina</Button>
                    </td>
                  </tr>
                ))}
                {scrapingSchedules.length === 0 && (
                  <tr>
                    <td className="py-3" colSpan={7} style={{ color: COLORS.text2 }}>Nessuna schedulazione scraping.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard
          title="Schedulazioni email (notifiche)"
          description="Vista admin di tutte le schedulazioni email (per utente). Puoi modificare, toggle e run-now."
        >
          {notifEdit && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: COLORS.bg }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Nome</label>
                  <input className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }}
                    value={notifEdit.name} onChange={(e) => setNotifEdit((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Tipo schedulazione</label>
                  <select className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }}
                    value={notifEdit.schedule_type} onChange={(e) => setNotifEdit((p) => ({ ...p, schedule_type: e.target.value }))}>
                    <option value="daily">daily</option>
                    <option value="weekly">weekly</option>
                    <option value="custom">custom</option>
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Ora</label>
                    <input type="number" min="0" max="23" className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }}
                      value={notifEdit.hour} onChange={(e) => setNotifEdit((p) => ({ ...p, hour: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Minuto</label>
                    <input type="number" min="0" max="59" className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }}
                      value={notifEdit.minute} onChange={(e) => setNotifEdit((p) => ({ ...p, minute: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Giorno</label>
                    <input className="w-full px-3 py-2 rounded-lg border text-sm mt-1" style={{ borderColor: COLORS.border }}
                      value={notifEdit.day_of_week} onChange={(e) => setNotifEdit((p) => ({ ...p, day_of_week: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Custom cron</label>
                  <div className="mt-1">
                    <CronForm value={notifEdit.custom_cron} onChange={(v) => setNotifEdit((p) => ({ ...p, custom_cron: v }))} />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input type="checkbox" checked={notifEdit.is_active} onChange={(e) => setNotifEdit((p) => ({ ...p, is_active: e.target.checked }))} />
                    <span className="text-xs" style={{ color: COLORS.text2 }}>Attiva</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: COLORS.text2 }}>Filters JSON (opzionale)</label>
                  <textarea
                    className="w-full px-3 py-2 rounded-lg border text-xs mt-1"
                    style={{ borderColor: COLORS.border, minHeight: 120 }}
                    placeholder='{"regioni":["Campania"],"province":[],"cpv_codes":[],"soa_codes":[]}'
                    value={notifEdit.filters_json}
                    onChange={(e) => setNotifEdit((p) => ({ ...p, filters_json: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-3 flex gap-3">
                <Button onClick={saveNotifEdit}>Salva</Button>
                <Button variant="outline" onClick={() => setNotifEdit(null)}>Chiudi</Button>
              </div>
            </div>
          )}

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: COLORS.text2 }}>
                  <th className="py-2 pr-4">Utente</th>
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">Cron</th>
                  <th className="py-2 pr-4">Attiva</th>
                  <th className="py-2 pr-4">Last run</th>
                  <th className="py-2 pr-4">Success</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {notificationSchedules.map((n) => (
                  <tr key={n.id} className="border-t" style={{ borderColor: COLORS.border }}>
                    <td className="py-2 pr-4">{n.user_email}</td>
                    <td className="py-2 pr-4">{n.name}</td>
                    <td className="py-2 pr-4"><code>{n.cron_expression}</code></td>
                    <td className="py-2 pr-4">{n.is_active ? 'SI' : 'NO'}</td>
                    <td className="py-2 pr-4">{n.last_run || '—'}</td>
                    <td className="py-2 pr-4">{n.last_run_success === true ? 'OK' : (n.last_run_success === false ? 'KO' : '—')}</td>
                    <td className="py-2 pr-4 flex gap-2">
                      <Button variant="outline" onClick={() => openNotifEdit(n)}>Modifica</Button>
                      <Button variant="outline" onClick={() => toggleNotifSchedule(n.id)}>Toggle</Button>
                      <Button variant="outline" onClick={() => runNotifNow(n.id)}>Run-now</Button>
                    </td>
                  </tr>
                ))}
                {notificationSchedules.length === 0 && (
                  <tr>
                    <td className="py-3" colSpan={7} style={{ color: COLORS.text2 }}>Nessuna schedulazione email trovata.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard
          title="Archiviazione avvisi/lotti scaduti"
          description="Archivia automaticamente i lotti con deadline passata (status: active → archived)."
        >
          <div className="flex items-center gap-3 mb-3">
            <Button onClick={runArchiveNow} disabled={loading}>Archivia scaduti (run-now)</Button>
            <Button variant="outline" onClick={loadAll} disabled={loading}>Ricarica</Button>
          </div>
          <div className="text-sm" style={{ color: COLORS.text2 }}>
            {archiveStats ? (
              <ul className="list-disc pl-6">
                <li>Attivi: <strong style={{ color: COLORS.text }}>{archiveStats.active}</strong></li>
                <li>Archiviati: <strong style={{ color: COLORS.text }}>{archiveStats.archived}</strong></li>
                <li>Da archiviare (scaduti): <strong style={{ color: COLORS.text }}>{archiveStats.pending_archive}</strong></li>
                <li>Totale: <strong style={{ color: COLORS.text }}>{archiveStats.total}</strong></li>
              </ul>
            ) : (
              <span>—</span>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

